<template>
  <LcCard>
    <div class="mb-200">
      <h4 class="headline-h5-headline">
        {{ $t('account.payout.input.headline') }}
      </h4>
      <span class="paragraph-small-text">
        {{ $t('account.payout.input.text') }}
      </span>
    </div>

    <section class="balance-container bg-secondary-secondary-bold p-200 -mb-025">
      <span class="mr-100">
        {{ $t('account.payout.payablebalance') }}
      </span>
      <strong
        :class="{
          'text-accent-accent-danger': showErrorForMaxValue,
        }"
      >
        {{ formatMoney(max) }}
      </strong>
    </section>

    <LcForm
      ref="form"
      v-bind="{ validationSchema }"
      :tracking="{ containerId: 'payout_amount_form' }"
    >
      <LcFormField v-slot="{ field }" name="amount">
        <LcMoneyInput
          v-bind="field"
          v-model:value="amount"
          :disabled="max.value <= 0"
          :label="$t('account.payout.input.amount.label')"
        />
      </LcFormField>
    </LcForm>
  </LcCard>
</template>

<script lang="ts" setup>
import type { ComponentExposed } from 'vue-component-type-helpers'

import * as Yup from 'yup'

import type { Money } from '#api/shared/@types/generated/backend/graphql-schema-types'

import LcForm from '#tracking/app/component-overrides/LcForm.vue'

type FormValues = { amount: number }

type PayoutAmountFormProps = { max: Money; min: Money }

const { max, min } = defineProps<PayoutAmountFormProps>()
const amount = defineModel<Maybe<number> | undefined>('amount', {
  default: undefined,
  type: Number,
})

const { t } = useI18n()

const formRef = useTemplateRef<ComponentExposed<typeof LcForm<FormValues>>>('form')

const showErrorForMaxValue = computed(() => {
  const form = formRef.value?.internal
  if (!form) return false
  return form.values.amount > max.value
})

const validationSchema = Yup.object<FormValues>().shape({
  amount: Yup.number()
    .required()
    .test(
      'max',
      () =>
        t('validation.payout.maxamount', {
          maxAmount: formatMoney(max),
        }),
      (value) => value <= max.value,
    )
    .test(
      'min',
      () =>
        t('validation.payout.minamount', {
          minAmount: formatMoney(min),
        }),
      (value) => value >= min.value,
    ),
})

defineExpose({
  validate: () => formRef.value?.internal?.validate(),
})
</script>

<style lang="scss" scoped>
.balance-container {
  border-top-left-radius: token('size/radius/m');
  border-top-right-radius: token('size/radius/m');
}
</style>
